import { lazy } from "react";
import { Navigate } from "react-router-dom";
import { PERMISSIONS_LIST } from "utils/constants";

// Named exports can't be passed to lazy() directly - it expects a `default` export.
const lazyNamed = (loader, name) => lazy(() => loader().then((mod) => ({ default: mod[name] })));

const AdminLayout = lazyNamed(() => import("layout/AdminLayout"), "AdminLayout");
const ClientLayout = lazyNamed(() => import("layout/ClientLayout"), "ClientLayout");
const UserLayout = lazyNamed(() => import("layout/UserLayout"), "UserLayout");

const AgentProfile = lazyNamed(() => import("pages/admin/AgentProfile"), "AgentProfile");
const AgentsPage = lazyNamed(() => import("pages/admin/AgentsPage"), "AgentsPage");
const AgentSinglePage = lazyNamed(() => import("pages/admin/AgentsSinglePage"), "AgentSinglePage");
const ArchivedAgentsPage = lazyNamed(() => import("pages/admin/ArchivedAgentsPage"), "ArchivedAgentsPage");
const ArchivedPage = lazyNamed(() => import("pages/admin/ArchivedPage"), "ArchivedPage");
const ArchivedSinglePage = lazyNamed(() => import("pages/admin/ArchivedSinglePage"), "ArchivedSinglePage");
const ClientEvidencePage = lazyNamed(() => import("pages/admin/ClientEvidencePage"), "ClientEvidencePage");
const ClientEvidenceSinglePage = lazyNamed(
  () => import("pages/admin/ClientEvidenceSinglePage"),
  "ClientEvidenceSinglePage"
);
// const DashboardPage = lazyNamed(() => import("pages/admin/DashboardPage"), "DashboardPage");
const DashboardSinglePage = lazyNamed(() => import("pages/admin/DashboardSinglePage"), "DashboardSinglePage");
const DiscountsPage = lazyNamed(() => import("pages/admin/DiscountsPage"), "DiscountsPage");
const DocumentsPage = lazyNamed(() => import("pages/admin/DocumentsPage"), "DocumentsPage");
const DocumentsSinglePage = lazyNamed(() => import("pages/admin/DocumentsSiglePage"), "DocumentsSinglePage");
const EvidencePage = lazyNamed(() => import("pages/admin/EvidencePage"), "EvidencePage");
const EvidenceSinglePage = lazyNamed(() => import("pages/admin/EvidenceSinglePage"), "EvidenceSinglePage");
const EvidenceSinglePageV2 = lazyNamed(() => import("pages/admin/EvidenceSinglePageV2"), "EvidenceSinglePageV2");
const HearingDatesPages = lazyNamed(() => import("pages/admin/HearingDatesPages"), "HearingDatesPages");
const AdminHomePage = lazyNamed(() => import("pages/admin/HomePage"), "AdminHomePage");
const LeadsPage = lazyNamed(() => import("pages/admin/LeadsPage"), "LeadsPage");
const MailingTabPage = lazyNamed(() => import("pages/admin/MailingTabPage"), "MailingTabPage");
const MassPrintHistoryPage = lazyNamed(() => import("pages/admin/MassPrintHistoryPage"), "MassPrintHistoryPage");
const MaverickSettingsPage = lazyNamed(() => import("pages/admin/MaverickSettingsPage"), "MaverickSettingsPage");
const OwnersListPage = lazyNamed(() => import("pages/admin/OwnersListPage"), "OwnersListPage");
const OwnersSinglePage = lazyNamed(() => import("pages/admin/OwnersSinglePage"), "OwnersSinglePage");
const PaymentPage = lazyNamed(() => import("pages/admin/PaymentPage"), "PaymentPage");
const PdfEditorPage = lazyNamed(() => import("pages/admin/PdfEditorPage"), "PdfEditorPage");
const PropertyIndexPage = lazyNamed(() => import("pages/admin/PropertyIndexPage"), "PropertyIndexPage");
const PropertyProfilePage = lazyNamed(() => import("pages/admin/PropertyProfilePage"), "PropertyProfilePage");
const ProtestedAccountsPage = lazyNamed(() => import("pages/admin/ProtestedAccountsPage"), "ProtestedAccountsPage");
const SettingsPage = lazyNamed(() => import("pages/admin/SettingsPage"), "SettingsPage");
const AdminSignInPage = lazyNamed(() => import("pages/admin/SigninPage"), "AdminSignInPage");
const TasksPage = lazyNamed(() => import("pages/admin/TasksPage"), "TasksPage");
const TaskTemplatesPage = lazyNamed(() => import("pages/admin/TaskTemplatesPage"), "TaskTemplatesPage");

const AccountPage = lazyNamed(() => import("pages/client/AccountPage"), "AccountPage");
const AddPropertyPage = lazyNamed(() => import("pages/client/AddPropertyPage"), "AddPropertyPage");
const AppealsPage = lazyNamed(() => import("pages/client/AppealsPage"), "AppealsPage");
const ClientReferralPage = lazyNamed(() => import("pages/client/ClientReferralPage"), "ClientReferralPage");
const ContactUsPage = lazyNamed(() => import("pages/client/ContactUsPage"), "ContactUsPage");
const HomePage = lazyNamed(() => import("pages/client/HomePage"), "HomePage");
const InvoicesPage = lazyNamed(() => import("pages/client/InvoicesPage"), "InvoicesPage");
const OwnerPortalEvidencePageWrapper = lazyNamed(
  () => import("pages/client/OwnerPortalEvidencePage"),
  "OwnerPortalEvidencePageWrapper"
);
const OwnerPortalRenewDocuments = lazyNamed(
  () => import("pages/client/OwnerPortalRenewDocuments"),
  "OwnerPortalRenewDocuments"
);
const PrivacyPolicy = lazyNamed(() => import("pages/client/PrivacyPolicyPage"), "PrivacyPolicy");
const PropertiesPage = lazyNamed(() => import("pages/client/PropertiesPage"), "PropertiesPage");
const ReferralPage = lazyNamed(() => import("pages/client/ReferralPage"), "ReferralPage");
const SigninPage = lazyNamed(() => import("pages/client/SigninPage"), "SigninPage");
const SignupPageNew = lazyNamed(() => import("pages/client/signup-page-new"), "SignupPageNew");
const SubmitEvidencePage = lazyNamed(() => import("pages/client/SubmitEvidencePage"), "SubmitEvidencePage");

export const routes = {
  home: "/",
  signup: "/signup",
  signin: "/signin",
  notFound: "/NotFound",
  referrals: "/referrals",
  submitEvidence: "/submit-evidence",
  docusignRedirect: "/callback/docusign",
  privacyPolicy: "/privacy-policy",
};

export const adminRoutes = {
  admin: "admin/*",
  prefix: "/admin",
  signIn: "/admin/login",
  home: "home",
  dashboard: "dashboard",
  dashboardSinglePage: "dashboard/:id",
  owners: "owners",
  tasks: "tasks",
  taskTemplates: "tasks/templates",
  ownersSinglePage: "owners/:id/:selectedPropertyId",
  leads: "leads",
  evidence: "evidence",
  evidenceSinglePage: "evidence/:propertyId",
  maverick2SinglePage: "maverick2/:propertyId",
  massPrintHistory: "mass-print-history",
  documents: "documents",
  documentsSinglePage: "documents/:id",
  propertyIndex: "property-index",
  propertyProfile: "property/:propertyId",
  propertyProfileByAccount: "property/:county/:accountNumber",
  leadsSinglePage: "leads/:id",
  protestedAccounts: "protestedaccounts",
  hearingDates: "hearingdates",
  archived: "archived",
  archivedSinglePage: "archived/:id/:selectedPropertyId",
  clientEvidence: "client-evidence",
  clientEvidenceSinglePage: "client-evidence/:id",
  agents: "agents",
  agentSinglePage: "agents/:id",
  archivedAgents: "archived-agents",
  mailingTab: "mailing-tab",
  maverickSettings: "maverick-settings",
  discounts: "discounts",
  payment: "payment",
  printPacketSettings: "print-packet-settings",
  agentProfile: "agent-profile",
  pdfEditor: "pdf-editor/owner/:ownerId/document/:docId",
};

export const clientRoutes = {
  prefix: "/client/",
  client: "client/*",
  account: "account",
  properties: "properties",
  propertySinglePage: "properties/:id",
  appeals: "appeals",
  invoices: "invoices",
  addProperty: "add-property",
  submitEvidence: "submit-evidence",
  submitEvidenceId: "submit-evidence/:id",
  contactUs: "contact-us",
  renewDocuments: "renew-documents",
  renewDocumentsById: "renew-documents/:id",
  referrals: "referrals",
};

export const appRoutes = [
  {
    layout: <UserLayout />,
    path: "",
    children: [
      {
        component: <HomePage />,
        path: routes.home,
      },
      {
        component: <SignupPageNew />,
        path: routes.signup,
      },
      {
        component: <ReferralPage />,
        path: routes.referrals,
      },
      {
        component: <SubmitEvidencePage />,
        path: routes.submitEvidence,
      },
      {
        component: <SigninPage />,
        path: routes.signin,
      },
      {
        component: <AdminSignInPage />,
        path: adminRoutes.signIn,
      },
      {
        component: <PrivacyPolicy />,
        path: routes.privacyPolicy,
      },
    ],
  },
  {
    layout: <ClientLayout />,
    path: clientRoutes.client,
    children: [
      {
        component: <AppealsPage />,
        path: clientRoutes.appeals,
      },
      {
        component: <AccountPage />,
        path: clientRoutes.account,
      },
      {
        component: <InvoicesPage />,
        path: clientRoutes.invoices,
      },
      {
        component: <PropertiesPage />,
        path: clientRoutes.properties,
      },
      {
        component: <AddPropertyPage />,
        path: clientRoutes.addProperty,
      },
      {
        component: <OwnerPortalEvidencePageWrapper />,
        path: clientRoutes.submitEvidenceId,
      },
      {
        component: <ContactUsPage />,
        path: clientRoutes.contactUs,
      },
      {
        component: <OwnerPortalRenewDocuments />,
        path: clientRoutes.renewDocumentsById,
      },
      {
        component: <Navigate to={clientRoutes.appeals} />,
        path: "",
        index: true,
      },
      {
        component: <ClientReferralPage />,
        path: clientRoutes.referrals,
      },
    ],
  },
  {
    layout: <AdminLayout />,
    path: adminRoutes.admin,
    children: [
      {
        component: <AdminHomePage />,
        fallback: null,
        path: adminRoutes.home,
        permissions: [PERMISSIONS_LIST.accessHome],
      },
      // {
      //   component: <DashboardPage />,
      //   path: adminRoutes.dashboard,
      //   permissions: [PERMISSIONS_LIST.accessDashboard],
      // },
      {
        component: <DashboardSinglePage />,
        path: adminRoutes.dashboardSinglePage,
      },
      {
        component: <OwnersListPage />,
        path: adminRoutes.owners,
        permissions: [PERMISSIONS_LIST.accessOwners],
      },
      {
        component: <TasksPage />,
        path: adminRoutes.tasks,
        permissions: [PERMISSIONS_LIST.manageTasks],
      },
      {
        component: <TaskTemplatesPage />,
        path: adminRoutes.taskTemplates,
        permissions: [PERMISSIONS_LIST.manageTasks],
      },
      {
        component: <EvidencePage />,
        path: adminRoutes.evidence,
        permissions: [PERMISSIONS_LIST.accessEvidence],
      },
      {
        component: <EvidenceSinglePage />,
        path: adminRoutes.evidenceSinglePage,
        permissions: [PERMISSIONS_LIST.accessEvidence],
      },
      {
        component: <EvidenceSinglePageV2 />,
        path: adminRoutes.maverick2SinglePage,
        permissions: [PERMISSIONS_LIST.accessMaverick2, PERMISSIONS_LIST.adminPanel],
      },
      {
        component: <OwnersSinglePage />,
        path: adminRoutes.ownersSinglePage,
        permissions: [PERMISSIONS_LIST.accessOwners],
      },
      {
        component: <OwnersSinglePage leads />,
        path: adminRoutes.leadsSinglePage,
        permissions: [PERMISSIONS_LIST.accessLeads],
      },
      {
        component: <LeadsPage />,
        path: adminRoutes.leads,
        permissions: [PERMISSIONS_LIST.accessLeads],
      },
      {
        component: <DocumentsPage />,
        path: adminRoutes.documents,
        permissions: [PERMISSIONS_LIST.accessDocuments],
      },
      {
        component: <DocumentsSinglePage />,
        path: adminRoutes.documentsSinglePage,
        permissions: [PERMISSIONS_LIST.accessDocuments],
      },
      {
        component: <PropertyIndexPage />,
        path: adminRoutes.propertyIndex,
        permissions: [PERMISSIONS_LIST.accessPropertyDocuments],
      },
      {
        // No permissions key: viewable by any authenticated admin user, same as dashboardSinglePage above.
        component: <PropertyProfilePage />,
        path: adminRoutes.propertyProfile,
      },
      {
        component: <PropertyProfilePage />,
        path: adminRoutes.propertyProfileByAccount,
      },
      {
        component: <ProtestedAccountsPage />,
        path: adminRoutes.protestedAccounts,
        permissions: [PERMISSIONS_LIST.accessProtestedAccounts],
      },
      {
        component: <HearingDatesPages />,
        path: adminRoutes.hearingDates,
        permissions: [PERMISSIONS_LIST.accessHearingDates],
      },
      {
        component: <ArchivedPage />,
        path: adminRoutes.archived,
        permissions: [PERMISSIONS_LIST.accessArchived],
      },
      {
        component: <ArchivedSinglePage />,
        path: adminRoutes.archivedSinglePage,
        permissions: [PERMISSIONS_LIST.accessArchived],
      },
      {
        component: <MassPrintHistoryPage />,
        path: adminRoutes.massPrintHistory,
        permissions: [PERMISSIONS_LIST.generateMassPrints],
      },
      {
        component: <ClientEvidencePage />,
        path: adminRoutes.clientEvidence,
        permissions: [PERMISSIONS_LIST.accessClientEvidence],
      },
      {
        component: <ClientEvidenceSinglePage />,
        path: adminRoutes.clientEvidenceSinglePage,
        permissions: [PERMISSIONS_LIST.accessClientEvidence],
      },
      {
        component: <AgentsPage />,
        path: adminRoutes.agents,
        permissions: [PERMISSIONS_LIST.accessAgents],
      },
      {
        component: <AgentSinglePage />,
        path: adminRoutes.agentSinglePage,
        permissions: [
          PERMISSIONS_LIST.agentActionTracker,
          PERMISSIONS_LIST.downloadActionCSV,
          PERMISSIONS_LIST.deleteUsers,
          PERMISSIONS_LIST.updateUsers,
        ],
      },
      {
        component: <ArchivedAgentsPage />,
        path: adminRoutes.archivedAgents,
        permissions: [PERMISSIONS_LIST.accessAgents],
      },
      {
        component: <MailingTabPage />,
        path: adminRoutes.mailingTab,
        permissions: [PERMISSIONS_LIST.accessMailingPage],
      },
      {
        component: <MaverickSettingsPage />,
        path: adminRoutes.maverickSettings,
        permissions: [PERMISSIONS_LIST.accessMaverickSettings, PERMISSIONS_LIST.adminPanel],
      },
      {
        component: <DiscountsPage />,
        path: adminRoutes.discounts,
      },
      {
        // No `permissions`: every authenticated agent may edit their own
        // account. Gating this on a permission is the one thing that would
        // break it -- the people most likely to lack any given permission are
        // exactly the ones who still need to change their own password.
        component: <AgentProfile />,
        path: adminRoutes.agentProfile,
      },
      {
        component: <PaymentPage />,
        path: adminRoutes.payment,
      },
      {
        component: <SettingsPage />,
        path: adminRoutes.printPacketSettings,
        permissions: [PERMISSIONS_LIST.accessSettings],
      },
      {
        component: <PdfEditorPage />,
        path: adminRoutes.pdfEditor,
      },
    ],
  },
];
